import { d as debounce } from './debounce-CFtxQv9K.js';

/*
Toggle class on another element.

HTML Usage:
<toggle-element-class data-options='{}'>
  Toggle element class button
</toggle-element-class>
*/


class ToggleElementClass extends HTMLElement {
  constructor() {
    super();

    // Options
    this.options = {
      toggleClass: 'toggle--is-active',
      toggleTriggerClass: '',
      target: 'body',
      onHover: false,
      hoverDelay: 0,
      preventDefault: true,
      stopPropagation: true,
      ariaExpanded: false,
    };

    // Get options from element data and combine with this.options
    const dataOptions = JSON.parse(this.dataset?.options || '{}');
    this.options = {
      ...this.options,
      ...dataOptions,
    };

    // Split toggleClass and toggleTriggerClass by space to support multiple classes
    if (this.options.toggleClass.length > 0) {
      this.options.toggleClass = this.options.toggleClass.split(' ');
    } else {
      this.options.toggleClass = [];
    }
    if (this.options.toggleTriggerClass.length > 0) {
      this.options.toggleTriggerClass = this.options.toggleTriggerClass.split(' ');
    } else {
      this.options.toggleTriggerClass = [];
    }

    // Reduce actions (So the event can also be removed)
    this.reducer = {
      click: (e) => this.debounceClickEvent(e),
      mouseEnter: (e) => this.debouncedOnMouse(e, 'add'),
      mouseLeave: (e) => this.debouncedOnMouse(e, 'remove'),
    };

    // Construct webcomponent
    this.construct();
  }

  /*
      Construct webcomponent
    */
  construct() {
    this.target = document.querySelector(this.options.target);

    if (!this.target) return false;

    // Click
    this.removeEventListener('click', this.reducer.click);
    this.addEventListener('click', this.reducer.click);

    // Mouse enter and leave
    if (this.options.onHover) {
      this.removeEventListener('mouseenter', this.reducer.mouseEnter);
      this.addEventListener('mouseleave', this.reducer.mouseLeave);
    }
  }

  /*
      Debounce click event
      @param event {object}: mouseevent
      Called by add event listener -> reducer.
    */
  debounceClickEvent(event) {
    console.log('click');
    if (!event) return false;

    this.preventDefault(event);
    this.stopPropagation(event);
    this.toggle();
  }

  /*
      Debounced mouse event
      @param event {object}: mouseevent
      @param type {string}: open/close/closeAll
      Called by add event listener -> reducer.
    */
  debouncedOnMouse(event, type) {
    if (!event) return false;

    const debouncer = debounce((type) => {
      switch (type) {
        case 'add':
          this.add();
          break;
        case 'remove':
          this.remove();
          break;
        default:
          this.toggle();
          break;
      }
    }, this.options.hoverDelay);
    debouncer(type);
  }

  preventDefault(event) {
    if (this.options.preventDefault) event.preventDefault();
  }

  stopPropagation(event) {
    if (this.options.stopPropagation) event.stopPropagation();
  }

  add() {
    const { toggleClass, toggleTriggerClass } = this.options;

    if (this.target && toggleClass.length > 0) this.target.classList.add(...toggleClass);

    if (toggleTriggerClass.length > 0) this.classList.add(...toggleTriggerClass);

    // Switch aria-expanded
    if (this.options.ariaExpanded) this.setAttribute('aria-expanded', true);
  }

  remove() {
    const { toggleClass, toggleTriggerClass } = this.options;
    if (this.target && toggleClass.length > 0) this.target.classList.remove(...toggleClass);

    if (toggleTriggerClass.length > 0) this.classList.remove(...toggleTriggerClass);

    // Switch aria-expanded
    if (this.options.ariaExpanded) this.setAttribute('aria-expanded', false);
  }

  toggle() {
    const { toggleClass } = this.options;
    if (!this.target) return false;

    !this.target.classList.contains(toggleClass[0]) ? this.add() : this.remove();
  }
}
if (!customElements.get('toggle-element-class')) {
  window.ToggleElementClass = ToggleElementClass;
  customElements.define('toggle-element-class', ToggleElementClass);
}
